import React, { useEffect, useState } from "react";
import { socketService } from "../../../services/socket";
import { authFetch } from "../../../services/authFetch";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

export const AIQueueManager: React.FC = () => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<{
    waiting: any[];
    active: any[];
    completed: any[];
    failed: any[];
  }>({
    waiting: [],
    active: [],
    completed: [],
    failed: [],
  });
  const [loading, setLoading] = useState(true);
  const [enableRollingSummaries, setEnableRollingSummaries] = useState(false);

  const toggleRollingSummaries = () => {
    const next = !enableRollingSummaries;
    setEnableRollingSummaries(next);
    socketService.emit("update-project-setting", {
      key: "enableRollingSummaries",
      value: next,
    });
  };

  useEffect(() => {
    const handleInit = (data: any) => {
      if (data?.projectSettings) {
        setEnableRollingSummaries(data.projectSettings.enableRollingSummaries === true);
      }
    };
    const handleSettingUpdated = (data: { key: string; value: any }) => {
      if (data.key === "enableRollingSummaries") {
        setEnableRollingSummaries(data.value === true);
      }
    };
    socketService.on("init-data", handleInit);
    socketService.on("project-setting-updated", handleSettingUpdated);
    return () => {
      socketService.off("init-data", handleInit);
      socketService.off("project-setting-updated", handleSettingUpdated);
    };
  }, []);

  const fetchQueue = async () => {
    try {
      const res = await authFetch("/api/admin/ai-queue");
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.tasks) {
          setTasks(data.tasks);
        }
      }
    } catch (err) {
      console.error("Failed to fetch AI queue", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();

    // Listen to real-time updates
    const handleQueueUpdate = (data: any) => {
      // Refreshing the whole queue on every update might be heavy, but it's safe for now.
      fetchQueue();
    };

    socketService.on("ai-queue-update", handleQueueUpdate);
    return () => {
      socketService.off("ai-queue-update", handleQueueUpdate);
    };
  }, []);

  const handleRetry = async (jobId: string) => {
    try {
      await authFetch("/api/admin/ai-queue/retry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId }),
      });
      // Immediately refresh queue
      fetchQueue();
    } catch (err) {
      console.error("Failed to retry job", err);
    }
  };

  const Section = ({ title, count, color, jobs, state }: any) => (
    <div
      style={{
        background: "rgba(0,0,0,0.3)",
        borderRadius: "12px",
        padding: "16px",
        border: `1px solid ${color}40`,
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h4 style={{ color, margin: 0, fontSize: "0.95rem", fontWeight: "bold" }}>
          {title} ({count})
        </h4>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {jobs.length === 0 ? (
          <div style={{ color: "#888", fontSize: "0.8rem", textAlign: "center", padding: "12px" }}>
            No hay tareas en este estado
          </div>
        ) : (
          jobs.map((job: any) => (
            <div
              key={job.id}
              style={{
                background: "rgba(255,255,255,0.05)",
                padding: "12px",
                borderRadius: "8px",
                borderLeft: `3px solid ${color}`,
                fontSize: "0.85rem",
              }}
            >
              <div
                style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}
              >
                <strong style={{ color: "#fff" }}>Job #{job.id}</strong>
                <span style={{ color: "#888", fontSize: "0.75rem" }}>
                  {new Date(job.timestamp).toLocaleString()}
                </span>
              </div>
              <div style={{ color: "#ccc", marginBottom: "8px" }}>
                Historia ID: {job.data?.storyId} <br />
                Proyecto ID: {job.data?.projectId}
              </div>
              {job.failedReason && (
                <div
                  style={{
                    color: "#EF4444",
                    fontSize: "0.8rem",
                    marginTop: "4px",
                    background: "rgba(239, 68, 68, 0.1)",
                    padding: "4px 8px",
                    borderRadius: "4px",
                  }}
                >
                  {job.failedReason}
                </div>
              )}
              {state === "failed" && (
                <Button
                  size="sm"
                  variant="outline"
                  style={{
                    marginTop: "8px",
                    borderColor: "#EF4444",
                    color: "#EF4444",
                    fontSize: "0.7rem",
                    height: "24px",
                  }}
                  onClick={() => handleRetry(job.id)}
                >
                  <i className="bx bx-refresh" style={{ marginRight: "4px" }}></i> Reintentar
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div
      style={{
        padding: "24px",
        color: "var(--color-text-primary)",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        height: "100%",
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ color: "#fff", margin: "0 0 4px 0" }}>Cola de Resúmenes AI</h2>
          <p style={{ color: "var(--color-text-muted)", margin: 0, fontSize: "0.9rem" }}>
            Monitoreo en tiempo real de las tareas de generación de resúmenes (BullMQ).
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            onClick={toggleRollingSummaries}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "6px 12px",
              borderRadius: "6px",
              cursor: "pointer",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid var(--color-panel-border)",
            }}
          >
            <span
              style={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: enableRollingSummaries ? "#E879A4" : "#888",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="bx bx-bar-chart-alt-2" style={{ fontSize: "1rem" }}></i>
              {t("controller.layout.rollingSummaries", "Rolling Summaries")}
            </span>
            <div
              style={{
                width: "34px",
                height: "18px",
                borderRadius: "9px",
                background: enableRollingSummaries ? "#E879A4" : "rgba(255,255,255,0.15)",
                position: "relative",
                transition: "background 0.2s",
              }}
            >
              <div
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  background: "#fff",
                  position: "absolute",
                  top: "2px",
                  left: enableRollingSummaries ? "18px" : "2px",
                  transition: "left 0.2s",
                }}
              />
            </div>
          </div>
          <Button variant="outline" onClick={fetchQueue} disabled={loading}>
            <i
              className={`bx bx-refresh ${loading ? "bx-spin" : ""}`}
              style={{ marginRight: "6px" }}
            ></i>
            Actualizar
          </Button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "16px",
        }}
      >
        <Section
          title="⏳ En Cola"
          count={tasks.waiting.length}
          color="#F59E0B"
          jobs={tasks.waiting}
          state="waiting"
        />
        <Section
          title="▶️ Procesando"
          count={tasks.active.length}
          color="#3B82F6"
          jobs={tasks.active}
          state="active"
        />
        <Section
          title="✅ Completados"
          count={tasks.completed.length}
          color="#10B981"
          jobs={tasks.completed}
          state="completed"
        />
        <Section
          title="❌ Fallidos"
          count={tasks.failed.length}
          color="#EF4444"
          jobs={tasks.failed}
          state="failed"
        />
      </div>
    </div>
  );
};
